import React, { useRef, useState } from 'react';
import { X, Upload, AlertTriangle, FileSpreadsheet, CheckCircle2, Trash2, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { apiPost, apiUpload } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BulkEmployeeImportModal
 * Shared modal used by User Management + HR Employee Master.
 * Upload an Employee Master .xlsx; optional wipe-first + create-logins.
 *
 * Props:
 *   open          – boolean
 *   onClose       – () => void
 *   onSuccess     – (result) => void  parent should refresh its list
 */
export default function BulkEmployeeImportModal({ open, onClose, onSuccess }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [wipeFirst, setWipeFirst] = useState(false);
  const [createLogins, setCreateLogins] = useState(true);
  // Always default the attendance month to the current month so re-imports never accidentally
  // drop daily attendance data. User can change or clear it explicitly if they only want the
  // employee master refreshed.
  const [attendanceMonth, setAttendanceMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setWipeFirst(false);
    setCreateLogins(true);
    setAttendanceMonth(new Date().toISOString().slice(0, 7));
    setResult(null);
    setConfirmText('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const downloadTemplate = async () => {
    // use fetch+blob so the Bearer token is included.
    // window.location.href would drop auth (cookies are stripped cross-origin).
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${API}/api/hr/employees/template.xlsx`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ellora-employee-bulk-import-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded — fill it in and re-upload here');
    } catch (e) {
      toast.error('Could not download template: ' + (e.message || ''));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select an .xlsx file');
      return;
    }
    if (wipeFirst && confirmText !== 'WIPE') {
      toast.error('Type "WIPE" to confirm system reset');
      return;
    }

    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    setSubmitting(true);
    try {
      // 1. Optional wipe
      if (wipeFirst) {
        await apiPost('/hr/employees/wipe-all?wipe_users=true', {});
      }

      // 2. Import
      const fd = new FormData();
      fd.append('file', file);
      const qs = new URLSearchParams();
      qs.set('create_logins', String(createLogins));
      if (attendanceMonth) qs.set('attendance_month', attendanceMonth);

      const data = await apiUpload(`/hr/employees/bulk-import?${qs.toString()}`, fd);
      setResult(data);
      toast.success(`Imported ${data.employees_imported} employees${data.users_created ? ` and created ${data.users_created} logins` : ''}.`);
      onSuccess?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="bulk-import-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-semibold">Bulk Employee Import</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="bulk-import-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <div className="px-6 py-5 space-y-5">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="text-amber-900 flex-1">
                Upload the <b>Ellora Employee Master.xlsx</b> (Sheet1). Expected header columns:
                Sr.No · Employee ID · Work Location · Employee Name · Designation · Department ·
                UAN · DOJ · DOC · Working Years · PAN · IFSC · Bank Acct · Annual CTC · Gross
                Salary · Total Working Days · Day 1-31 attendance grid · Email.
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-amber-300 rounded text-amber-800 hover:bg-amber-100 text-xs font-display font-bold uppercase tracking-wider"
                    data-testid="bulk-import-template-btn"
                  >
                    <Download className="w-3.5 h-3.5" /> Download Sample Template
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Excel File (.xlsx)</label>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm border border-slate-300 rounded px-3 py-2 file:mr-3 file:rounded file:border-0 file:bg-orange-50 file:text-orange-700 file:px-3 file:py-1.5"
                data-testid="bulk-import-file-input"
              />
              {file && (
                <p className="text-xs text-slate-600 mt-1">
                  Selected: <b>{file.name}</b> ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={createLogins}
                  onChange={(e) => setCreateLogins(e.target.checked)}
                  className="mt-1"
                  data-testid="bulk-import-create-logins-toggle"
                />
                <div className="text-sm">
                  <div className="font-medium">Create user login accounts</div>
                  <div className="text-slate-600">
                    Each employee gets an account with email
                    <code className="px-1 mx-1 bg-slate-100 rounded">empid@elloraepc.com</code>
                    and default password <code className="px-1 bg-slate-100 rounded">Ellora@8667</code>{' '}
                    (must change on first login).
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-red-200 bg-red-50/40 rounded-md cursor-pointer hover:bg-red-50">
                <input
                  type="checkbox"
                  checked={wipeFirst}
                  onChange={(e) => setWipeFirst(e.target.checked)}
                  className="mt-1"
                  data-testid="bulk-import-wipe-toggle"
                />
                <div className="text-sm flex-1">
                  <div className="font-medium text-red-700 flex items-center gap-2">
                    <Trash2 className="w-4 h-4" /> Wipe ALL existing employees & user accounts
                  </div>
                  <div className="text-slate-700">
                    Hard-deletes every employee, payroll record, CTC, leave, attendance, and user
                    login (super admin <code>omkar@elloraepc.com</code> is preserved). Cannot be undone.
                  </div>
                  {wipeFirst && (
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder='Type "WIPE" to confirm'
                      className="mt-2 w-full px-2 py-1.5 border border-red-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="bulk-import-wipe-confirm-input"
                    />
                  )}
                </div>
              </label>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Attendance Month <span className="text-red-600">*</span>
                </label>
                <input
                  type="month"
                  value={attendanceMonth}
                  onChange={(e) => setAttendanceMonth(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm w-full max-w-xs"
                  data-testid="bulk-import-attendance-month-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Pre-filled with the current month so re-imports never accidentally drop daily attendance. Clear this field only if you intentionally want to skip attendance.
                </p>
                {!attendanceMonth && (
                  <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> No month selected — attendance grid will NOT be imported.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-800 mb-1">Import successful</div>
                <ul className="text-emerald-900 space-y-0.5">
                  <li>Employees imported: <b>{result.employees_imported}</b></li>
                  {!!result.users_created && (
                    <li>User logins created: <b>{result.users_created}</b></li>
                  )}
                  {!!result.attendance_records_imported && (
                    <li>Attendance rows: <b>{result.attendance_records_imported}</b> for {result.attendance_month}</li>
                  )}
                  {result.skipped_rows?.length > 0 && (
                    <li>Skipped rows: {result.skipped_rows.join(', ')}</li>
                  )}
                </ul>
              </div>
            </div>

            {result.default_password && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm flex items-center gap-2">
                <span>Default password assigned to all new logins:</span>
                <code className="px-2 py-1 bg-white border rounded font-mono">{result.default_password}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(result.default_password); toast.success('Copied'); }}
                  className="text-amber-700 hover:text-amber-900"
                  data-testid="bulk-import-copy-password-btn"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            )}

            {result.summary?.departments && (
              <div className="text-xs text-slate-600">
                <b>Departments detected:</b> {result.summary.departments.join(', ')}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t bg-slate-50">
          <Button variant="outline" onClick={handleClose} data-testid="bulk-import-cancel-btn">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleUpload}
              disabled={submitting || !file}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="bulk-import-submit-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              {submitting ? 'Importing…' : wipeFirst ? 'Wipe & Import' : 'Import Employees'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
